import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl, FormattedMessage } from 'react-intl';
import { Space, Button, Modal, Divider } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  DocumentTypeReduced,
  ListDocumentTypesQuery,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';

import { useDocumentTypesReduced } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import AccountsDocumentTypesReportModal from '@/components/Financial/Accounts/DocumentTypes/Report/AccountsDocumentTypesReportModal';
import AccountsDocumentTypesGeneralModalForm from '@/components/Financial/Accounts/DocumentTypes/General/ModalForm';
import { useDeleteDocumentType } from '../../../../core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import DocumentTypeExtraFilter from './DocumentTypeExtraFilters/index';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import Authorities from '@/types/Authority';
import { useDebounce } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function DocumentTypesPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListDocumentTypesQuery>({
    order: 'ASC',
    ...convertParams<ListDocumentTypesQuery>({
      description: 'string',
      initials: 'string',
      active: 'boolean',
    }),
  });

  const [modalReportVisible, setModalReportVisible] = useState(false);
  const [modalGeneralVisible, setModalGeneralVisible] = useState(false);

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useDocumentTypesReduced(queryDebounce);

  const documentTypes = flatInfiniteData(data);

  const toggleModalReport = () => {
    setModalReportVisible(!modalReportVisible);
  };

  const toggleModalGeneral = () => {
    setModalGeneralVisible(!modalGeneralVisible);
  };

  const removeMutation = useDeleteDocumentType();

  const handleRemoveClick = (document: DocumentTypeReduced) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'documentTypes.remove',
      }),
      content: (
        <p>
          Deseja deletar o Tipo de Documento "
          <strong>{document.description}</strong>
          "?
        </p>
      ),
      onOk: async () => {
        await removeMutation.mutateAsync(document.id);
      },
      okType: 'danger',
      width: 600,
    });
  };

  const columns: ColumnsType<DocumentTypeReduced> = [
    {
      dataIndex: 'initials',
      title: intl.formatMessage({ id: 'documentTypes.list.header.initials' }),
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'documentTypes.list.header.description',
      }),
    },
    {
      title: intl.formatMessage({ id: 'documentTypes.list.header.actions' }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/financial/accounts/documentTypes/${record.id}`}
            disabled={
              !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={
              !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_DELETE])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      route={route.parent}
      persistSearchParams={false}
      permissions={route.permissions}
      extra={
        <SpaceForButton>
          <Button
            disabled={
              !hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_REPORTS])
            }
            block
            type="primary"
            onClick={toggleModalReport}
          >
            <FormattedMessage id="documentTypes.generateReport" />
          </Button>

          <Button
            type="primary"
            block
            onClick={toggleModalGeneral}
            disabled={!hasPermissions([Authorities.REGISTER_DOCUMENT_TYPE_ADD])}
          >
            <FormattedMessage id="documentTypes.create" />
          </Button>
        </SpaceForButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, description: value }))
        }
        searchValue={queryParams.description}
        extraFilters={
          <DocumentTypeExtraFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<DocumentTypeReduced>
        columns={columns}
        dataSource={documentTypes}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <AccountsDocumentTypesReportModal
        visible={modalReportVisible}
        handleVisible={toggleModalReport}
      />

      <AccountsDocumentTypesGeneralModalForm
        visible={modalGeneralVisible}
        handleVisible={toggleModalGeneral}
      />
    </DefaultRouteTabs>
  );
}

export default DocumentTypesPage;
